import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import greencard from "../images/blogs/greencard.svg";
import { api_url } from "../config/config";

const steps = [
  {
    num: "01",
    title: "Login",
    desc: "Sign in to your MediSYS account to get started.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },
  {
    num: "02",
    title: "Upload Prescription",
    desc: "Take a clear photo of your doctor's prescription.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>
    ),
  },
  {
    num: "03",
    title: "Pharmacist Confirms",
    desc: "Our licensed pharmacist reviews and verifies your Rx.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.44 2 2 0 0 1 3.59 1.26h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.5a16 16 0 0 0 6 6l.97-.97a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.46 16z" />
      </svg>
    ),
  },
  {
    num: "04",
    title: "Doorstep Delivery",
    desc: "Medicines packed and delivered right to your door.",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="3" width="15" height="13" rx="1" />
        <path d="M16 8h4l3 3v5h-7V8z" />
        <circle cx="5.5" cy="18.5" r="2.5" />
        <circle cx="18.5" cy="18.5" r="2.5" />
      </svg>
    ),
  },
];

const Upload = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const check = () => setIsLoggedIn(!!localStorage.getItem("authToken"));
    check();
    window.addEventListener("authChanged", check);
    return () => window.removeEventListener("authChanged", check);
  }, []);

  const handleUploadClick = (e) => {
    if (!isLoggedIn) {
      e.preventDefault();
      toast.error("Please log in before uploading a prescription.");
    }
  };

  return (
    <section className="w-full font-poppins my-6 px-4 sm:px-6 lg:px-6 xl:px-6">
      <div
        className="relative rounded-3xl overflow-hidden"
        style={{ boxShadow: "0 8px 48px rgba(22,44,42,0.22)" }}
      >
        {/* ── Blurred bg layer ── */}
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(135deg, #0a1715 0%, #162c2a 45%, #1e4a46 100%)",
            filter: "blur(0px)",
          }}
        />

        {/* Blurred noise/texture overlay for frosted effect */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backdropFilter: "blur(2px)",
            background: "rgba(10,23,21,0.45)",
          }}
        />

        {/* Pharmacy cross watermark */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            opacity: 0.045,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='44' height='44' viewBox='0 0 44 44' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='18' y='8' width='8' height='28' rx='3' fill='white'/%3E%3Crect x='8' y='18' width='28' height='8' rx='3' fill='white'/%3E%3C/svg%3E")`,
            backgroundRepeat: "repeat",
            backgroundSize: "44px 44px",
          }}
        />

        {/* Glow blobs — blurred */}
        <div
          className="absolute -top-24 -right-24 w-96 h-96 rounded-full pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(95,189,179,0.28) 0%, transparent 65%)",
            filter: "blur(40px)",
          }}
        />
        <div
          className="absolute -bottom-16 left-[18%] w-80 h-80 rounded-full pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(255,96,30,0.18) 0%, transparent 65%)",
            filter: "blur(48px)",
          }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 right-[30%] w-48 h-48 rounded-full pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(95,189,179,0.12) 0%, transparent 65%)",
            filter: "blur(32px)",
          }}
        />

        {/* Teal shimmer line top */}
        <div
          className="absolute top-0 left-0 right-0 h-[2px] pointer-events-none"
          style={{ background: "linear-gradient(to right, transparent, #5fbdb3, #ff601e, transparent)" }}
        />

        {/* ── Content ── */}
        <div className="relative flex flex-col lg:flex-row">

          {/* ════ LEFT — Upload CTA ════ */}
          <div
            className="lg:w-[40%] flex-shrink-0 p-7 sm:p-9 lg:p-10 flex flex-col gap-7"
            style={{ borderRight: "1px solid rgba(255,255,255,0.07)" }}
          >
            {/* Badge + heading */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div
                  className="flex items-center gap-2 px-3 py-1 rounded-full"
                  style={{
                    background: "rgba(95,189,179,0.12)",
                    border: "1px solid rgba(95,189,179,0.25)",
                  }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="#5fbdb3">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                  </svg>
                  <span
                    className="text-[10px] font-bold uppercase tracking-[.18em]"
                    style={{ color: "#5fbdb3" }}
                  >
                    Prescription Service
                  </span>
                </div>
              </div>

              <h2
                className="font-bold leading-tight mb-3"
                style={{
                  fontSize: "clamp(20px, 3vw, 28px)",
                  color: "#ffffff",
                  fontFamily: "'Nunito', sans-serif",
                }}
              >
                Order with{" "}
                <span
                  style={{
                    background: "linear-gradient(90deg, #5fbdb3, #7dd4cf)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Prescription
                </span>
              </h2>

              <p
                className="text-[14px] leading-relaxed"
                style={{ color: "rgba(255,255,255,0.7)", maxWidth: 300 }}
              >
                Upload your doctor's prescription and we'll have a licensed pharmacist verify and deliver the right medicines to your door.
              </p>
            </div>

            {/* Illustration + CTA */}
            <div className="flex items-center gap-6">
              {/* Prescription card */}
              <div className="relative flex-shrink-0">
                <div
                  className="w-[90px] h-[94px] rounded-2xl flex items-center justify-center"
                  style={{
                    background: "rgba(255,255,255,0.07)",
                    border: "1px solid rgba(255,255,255,0.13)",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  <img src={greencard} alt="Prescription" className="w-[68px] h-[72px] object-contain" />
                </div>
                <div
                  className="absolute -top-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center"
                  style={{
                    background: "#5fbdb3",
                    border: "2px solid #162c2a",
                    boxShadow: "0 2px 8px rgba(95,189,179,0.5)",
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <Link
                  to="/my-prescriptions"
                  onClick={handleUploadClick}
                  className="flex items-center gap-2 font-bold text-[13px] px-5 h-11 rounded-xl transition-all duration-200 whitespace-nowrap hover:-translate-y-0.5"
                  style={{
                    background: "#ff601e",
                    color: "#ffffff",
                    boxShadow: "0 4px 18px rgba(255,96,30,0.45)",
                    fontFamily: "'Nunito', sans-serif",
                    letterSpacing: "0.02em",
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Upload Prescription
                </Link>

                <div className="flex items-center gap-2">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5fbdb3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  <span className="text-[13px] font-medium" style={{ color: "rgba(255,255,255,0.5)" }}>
                    100% safe &amp; secure
                  </span>
                </div>
              </div>
            </div>

            {/* Accepted formats */}
            <div
              className="flex items-center gap-3 rounded-2xl px-4 py-3"
              style={{
                background: "rgba(255,255,255,0.05)",
                border: "1px solid rgba(255,255,255,0.08)",
                backdropFilter: "blur(6px)",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5fbdb3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18M9 21V9" />
              </svg>
              <span className="text-[13px]" style={{ color: "rgba(255,255,255,0.55)" }}>
                Accepts JPG, PNG, PDF &mdash; Max 10 MB per file
              </span>
            </div>
          </div>

          {/* ════ RIGHT — How it works ════ */}
          <div className="flex-1 p-7 sm:p-9 lg:p-10">

            <div className="flex items-center gap-3 mb-7">
              <div
                className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: "rgba(255,96,30,0.15)", border: "1px solid rgba(255,96,30,0.25)" }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ff601e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <h3
                className="font-bold text-[16px] sm:text-[18px]"
                style={{ color: "#ffffff", fontFamily: "'Nunito', sans-serif" }}
              >
                How does this work?
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {steps.map((step, i) => (
                <div
                  key={i}
                  className="group flex items-start gap-4 rounded-2xl p-4 transition-all duration-200"
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    backdropFilter: "blur(8px)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(95,189,179,0.09)";
                    e.currentTarget.style.border = "1px solid rgba(95,189,179,0.22)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                    e.currentTarget.style.border = "1px solid rgba(255,255,255,0.08)";
                  }}
                >
                  <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center"
                      style={{
                        background: "rgba(255,96,30,0.15)",
                        border: "1px solid rgba(255,96,30,0.28)",
                        color: "#ff8050",
                      }}
                    >
                      {step.icon}
                    </div>
                    <span
                      className="text-[9px] font-black tracking-widest"
                      style={{ color: "rgba(255,96,30,0.55)" }}
                    >
                      {step.num}
                    </span>
                  </div>

                  <div>
                    <p
                      className="font-bold text-[13.5px] mb-1 leading-snug"
                      style={{ color: "#ffffff", fontFamily: "'Nunito', sans-serif" }}
                    >
                      {step.title}
                    </p>
                    <p
                      className="text-[12px] leading-relaxed"
                      style={{ color: "rgba(255,255,255,0.6)" }}
                    >
                      {step.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Disclaimer */}
            <div
              className="flex items-start gap-3 mt-5 rounded-2xl px-4 py-3"
              style={{
                background: "rgba(95,189,179,0.07)",
                border: "1px solid rgba(95,189,179,0.16)",
                backdropFilter: "blur(6px)",
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#5fbdb3"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="flex-shrink-0 mt-0.5"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
              <p
                className="text-[13px] leading-relaxed"
                style={{ color: "rgba(255,255,255,0.55)" }}
              >
                Prescription medicines are dispensed only after pharmacist verification.
                A valid doctor's prescription (Rx) is mandatory for all prescription drugs.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Upload;